// Local, offline IBAN normalization + structural/MOD-97 validation for inline form UX.
//
// Deliberately UX-only: the backend (App\Services\Iban\IbanValidator, called from
// App\Rules\ValidIban inside App\Http\Requests\Sepa\StoreSepaSignatureRequest) is the
// authoritative check and is run again server-side regardless of what happens here. A passing
// result here means only that the value is structurally well-formed and its checksum is
// internally consistent - it never means the account exists, or that it belongs to anyone in
// particular, or that it can collect a direct debit.
//
// Two simplifications versus the backend validator, both intentional:
//   - No network call is ever made (matches the backend: this is a local computation only).
//   - Only the Netherlands' exact IBAN length (18) is enforced client-side; the full ~89-country
//     length registry lives server-side only (config/iban.php), so a non-NL IBAN with a
//     plausible-but-wrong-for-its-country length can pass here and will be caught authoritatively
//     by the backend - acceptable for a UX-only check, not a correctness gap in the app overall.

const MIN_LENGTH = 15; // shortest real IBAN (Norway)
const MAX_LENGTH = 34; // ISO 13616 maximum
const NL_LENGTH = 18;

/**
 * Trim, strip grouping spaces, uppercase - identical semantics to
 * App\Services\Iban\IbanNormalizer::normalize() on the backend.
 * @param {string} raw
 * @returns {string}
 */
export function normalizeIban(raw) {
  var value = raw == null ? '' : String(raw);
  return value.trim().replace(/ /g, '').toUpperCase();
}

/**
 * @param {string} canonical an already-normalized value (see normalizeIban)
 * @returns {boolean}
 */
export function isValidIban(canonical) {
  if (typeof canonical !== 'string' || canonical === '') {
    return false;
  }
  if (!/^[A-Z0-9]+$/.test(canonical)) {
    return false;
  }
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(canonical)) {
    return false;
  }
  if (canonical.length < MIN_LENGTH || canonical.length > MAX_LENGTH) {
    return false;
  }
  if (canonical.slice(0, 2) === 'NL' && canonical.length !== NL_LENGTH) {
    return false;
  }

  return mod97(canonical) === 1n;
}

/**
 * ISO 13616 MOD-97-10 via BigInt - no overflow risk, no library, supported by every evergreen
 * browser. Mirrors App\Services\Iban\IbanValidator::mod97()'s use of PHP's bcmod for the same
 * reason (a 34-character IBAN's numeric expansion exceeds a native 64-bit integer).
 * @param {string} canonical
 * @returns {bigint}
 */
function mod97(canonical) {
  var rearranged = canonical.slice(4) + canonical.slice(0, 4);
  var numeric = '';
  for (var i = 0; i < rearranged.length; i++) {
    var ch = rearranged[i];
    numeric += /[0-9]/.test(ch) ? ch : String(ch.charCodeAt(0) - 'A'.charCodeAt(0) + 10);
  }

  return BigInt(numeric) % 97n;
}

export default { normalizeIban: normalizeIban, isValidIban: isValidIban };
